import { useCallback, useEffect, useRef, useState } from 'react';
import { useSignMessage } from 'wagmi';
import {
    clearStoredBackendToken,
    connectWalletSessionWithSignature,
    getStoredBackendToken,
    storeBackendToken,
} from '../api/backendClient';
import { clearEncryptionKeyPairCache } from '../utils/walletTaskEncryption';

const isJwtExpired = (token) => {
    if (!token) return true;
    try {
        const parts = String(token).split('.');
        if (parts.length !== 3) return true;
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const json =
            typeof atob === 'function'
                ? atob(base64)
                : Buffer.from(base64, 'base64').toString('utf8');
        const payload = JSON.parse(json);
        if (!payload?.exp) return false;
        return Date.now() >= payload.exp * 1000 - 30_000;
    } catch (_error) {
        return true;
    }
};

/**
 * Registers the connected wallet with the CronPay backend using a signature.
 * APP-M08: generation counter ignores stale async completions after wallet switch.
 */
export function useBackendWalletAuth({
    walletAddress,
    enabled = true,
    rolePreference,
    autoSignIn = true,
} = {}) {
    const { signMessageAsync } = useSignMessage();
    const [status, setStatus] = useState('idle');
    const [token, setToken] = useState('');
    const [user, setUser] = useState(null);
    const [error, setError] = useState('');
    const signingRef = useRef(false);
    const generationRef = useRef(0);
    const walletRef = useRef(walletAddress);

    const bumpGeneration = useCallback(() => {
        generationRef.current += 1;
        return generationRef.current;
    }, []);

    const signIn = useCallback(
        async ({ force = false } = {}) => {
            const generation = generationRef.current;
            const expectedWallet = String(walletAddress || '')
                .trim()
                .toLowerCase();

            if (!walletAddress || !enabled) {
                if (generation === generationRef.current) {
                    setStatus('idle');
                    setToken('');
                    setUser(null);
                }
                return null;
            }

            if (signingRef.current) {
                return getStoredBackendToken(walletAddress);
            }

            const cached = getStoredBackendToken(walletAddress);
            if (!force && cached && !isJwtExpired(cached)) {
                if (generation !== generationRef.current) return null;
                if (
                    String(walletRef.current || '')
                        .trim()
                        .toLowerCase() !== expectedWallet
                ) {
                    return null;
                }
                setToken(cached);
                setStatus('ready');
                setError('');
                return cached;
            }

            if (cached) {
                clearStoredBackendToken(walletAddress);
            }

            signingRef.current = true;
            if (generation === generationRef.current) {
                setStatus('signing');
                setError('');
            }

            try {
                const session = await connectWalletSessionWithSignature({
                    walletAddress,
                    signMessageAsync,
                    rolePreference,
                });
                if (generation !== generationRef.current) {
                    return null;
                }
                if (
                    String(walletRef.current || '')
                        .trim()
                        .toLowerCase() !== expectedWallet
                ) {
                    return null;
                }

                const nextToken = session?.token;
                if (!nextToken) {
                    throw new Error('Backend did not return a session token');
                }
                storeBackendToken(walletAddress, nextToken);
                setToken(nextToken);
                setUser(session?.user || null);
                setStatus('ready');
                return nextToken;
            } catch (signInError) {
                if (generation !== generationRef.current) {
                    return null;
                }
                const message =
                    signInError?.response?.data?.message ||
                    signInError?.shortMessage ||
                    signInError?.message ||
                    'Failed to register wallet with the server';

                if (
                    String(message).toLowerCase().includes('user rejected') ||
                    String(message).toLowerCase().includes('denied')
                ) {
                    setError(
                        'Signature request was cancelled. Sign in to save payments and history.',
                    );
                } else {
                    setError(message);
                }
                setStatus('error');
                setToken('');
                setUser(null);
                return null;
            } finally {
                signingRef.current = false;
            }
        },
        [walletAddress, enabled, rolePreference, signMessageAsync],
    );

    useEffect(() => {
        const previous = walletRef.current;
        walletRef.current = walletAddress;
        bumpGeneration();
        signingRef.current = false;

        if (previous && previous !== walletAddress) {
            clearStoredBackendToken(previous);
            clearEncryptionKeyPairCache(previous);
        }

        if (!enabled || !walletAddress) {
            setStatus('idle');
            setToken('');
            setUser(null);
            setError('');
            if (!walletAddress && previous) {
                clearEncryptionKeyPairCache(previous);
            }
            return undefined;
        }

        if (autoSignIn) {
            signIn();
        }
        return undefined;
    }, [walletAddress, enabled, autoSignIn, signIn, bumpGeneration]);

    return {
        status,
        token,
        user,
        error,
        signIn,
        isReady: status === 'ready' && Boolean(token),
        isSigning: status === 'signing',
        sessionGeneration: generationRef.current,
    };
}
